import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

export const badgeVariants = cva(
  "inline-flex items-center gap-[6px] rounded-[12px] p-2 text-xs font-medium min-h-[24px]",
  {
    variants: {
      variant: {
        // DS core
        primary: "bg-primary text-primary-foreground ring-primary-500",
        secondary: "bg-secondary text-secondary-foreground",
        outline: "bg-transparent border border-border text-foreground",
        ghost: "bg-transparent text-foreground",
        destructive: "bg-destructive text-destructive-foreground text-base-red-70",
        // Hark tokens — via @theme color keys
        default:
          "border bg-badge-default-bg text-badge-default-fg border-badge-default-border",
        informative: "bg-badge-informative-bg text-badge-informative-fg",
        warning: "bg-badge-warning-bg text-badge-warning-fg text-foreground text-base-yellow-70",
        success: "bg-badge-success-bg text-badge-success-fg",
      },
      size: {
        md: "",
        sm: "text-[10px] px-2 py-0.5 min-h-[20px] rounded-[8px] tracking-[0.15px]",
      },
      tone: {
        solid: "",
        subtle: "",
      },
      truncate: {
        true: "overflow-hidden text-ellipsis whitespace-nowrap",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "destructive",
        tone: "subtle",
        class: "bg-red-950 border border-base-red-50 text-base-red-100",
      },
      {
        variant: "informative",
        tone: "subtle",
        class: "bg-base-blue-50/30 border border-base-blue-40",
      },
      {
        variant: "success",
        tone: "subtle",
        class: "bg-green-950 border border-green-700 text-green-100",
      },
      {
        variant: "warning",
        tone: "subtle",
        class: "bg-yellow-950 border border-base-yellow-50 text-base-yellow-100",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "md",
      tone: "solid",
      truncate: false,
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  truncate?: boolean;
}

const Badge = ({
  className,
  variant,
  size,
  tone,
  startIcon,
  endIcon,
  children,
  truncate,
  ...props
}: BadgeProps) => (
  <div
    className={cn(badgeVariants({ variant, size, tone, truncate }), className)}
    {...props}
  >
    {startIcon && <span className="flex items-center">{startIcon}</span>}
    {children != null && (
      <span
        className={cn(
          "min-w-0",
          truncate && "overflow-hidden text-ellipsis whitespace-nowrap"
        )}
      >
        {children}
      </span>
    )}
    {endIcon && <span className="flex items-center">{endIcon}</span>}
  </div>
);
export { Badge };
